// =============================================================================
// hooks/ai-console/useDecisions.ts — TanStack Query hooks para ai_decision_logs.
//
// Consome API F9-S02 (fonte de verdade:
// apps/api/src/modules/ai-console/decisions/schemas.ts):
//   GET /api/ai-console/decisions?conversation_id=&lead_id=&node_name=
//       &cursor=&id_cursor=&limit=
//   GET /api/ai-console/decisions/timeline?conversation_id=
//
// Contrato (F58-S04 / IA-06): os schemas abaixo espelham os da API campo a campo.
// Não adicionar campo que a API não envia — o Zod falha a página INTEIRA.
//
// LGPD (doc 17):
//   - Decisões chegam com dados mascarados pelo backend — UI não tenta de-mask.
//   - Nunca logar decision/context/PII em console.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '../../lib/api';

// ─── Schemas Zod (espelho de decisions/schemas.ts da API) ────────────────────

/**
 * Uma entrada individual de ai_decision_logs (decisionItemSchema da API).
 * `decision` é jsonb mascarado pelo backend — objeto de estrutura arbitrária.
 * Não-estrito de propósito: campo novo da API não derruba a tela.
 */
export const DecisionItemSchema = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  lead_id: z.string().uuid().nullable(),
  customer_id: z.string().uuid().nullable(),
  node_name: z.string(),
  intent: z.string().nullable(),
  prompt_key: z.string().nullable(),
  /** Texto livre na API (ex: "v3"), NÃO numérico. */
  prompt_version: z.string().nullable(),
  model: z.string().nullable(),
  tokens_in: z.number().int().nullable(),
  tokens_out: z.number().int().nullable(),
  latency_ms: z.number().int().nullable(),
  decision: z.record(z.unknown()),
  error: z.string().nullable(),
  correlation_id: z.string().uuid(),
  /** null = modelo sem entry em model_pricing. */
  cost_usd: z.number().nullable(),
  cost_brl: z.number().nullable(),
  created_at: z.string().datetime(),
});

/** listDecisionsResponseSchema da API. */
export const DecisionsListResponseSchema = z.object({
  data: z.array(DecisionItemSchema),
  next_cursor: z.string().datetime().nullable(),
  next_id_cursor: z.string().uuid().nullable(),
  total_on_page: z.number().int(),
});

/** timelineResponseSchema da API — objeto, NÃO array. */
export const TimelineResponseSchema = z.object({
  conversation_id: z.string().uuid(),
  data: z.array(DecisionItemSchema),
  total: z.number().int(),
});

// ─── Tipos exportados ─────────────────────────────────────────────────────────

export type DecisionItem = z.infer<typeof DecisionItemSchema>;
export type DecisionsListResponse = z.infer<typeof DecisionsListResponseSchema>;
export type TimelineResponse = z.infer<typeof TimelineResponseSchema>;

/** Filtros aceitos por GET /decisions (listDecisionsQuerySchema da API). */
export interface DecisionFilters {
  conversation_id?: string;
  lead_id?: string;
  node_name?: string;
  /** Cursor de paginação — enviar sempre junto com id_cursor. */
  cursor?: string;
  id_cursor?: string;
  limit?: number;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const decisionsQueryKeys = {
  all: ['ai-console', 'decisions'] as const,
  list: (filters: DecisionFilters) => [...decisionsQueryKeys.all, 'list', filters] as const,
  timeline: (conversationId: string) =>
    [...decisionsQueryKeys.all, 'timeline', conversationId] as const,
} as const;

// ─── Fetch helpers ────────────────────────────────────────────────────────────

export function buildListQs(filters: DecisionFilters): string {
  const params = new URLSearchParams();

  if (filters.conversation_id) params.set('conversation_id', filters.conversation_id);
  if (filters.lead_id) params.set('lead_id', filters.lead_id);
  if (filters.node_name) params.set('node_name', filters.node_name);
  // A API só pagina com o par (cursor, id_cursor): sem o desempate, ignora o cursor.
  if (filters.cursor && filters.id_cursor) {
    params.set('cursor', filters.cursor);
    params.set('id_cursor', filters.id_cursor);
  }
  if (filters.limit) params.set('limit', String(filters.limit));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchDecisionsList(filters: DecisionFilters): Promise<DecisionsListResponse> {
  const qs = buildListQs(filters);
  const raw = await api.get<unknown>(`/api/ai-console/decisions${qs}`);
  return DecisionsListResponseSchema.parse(raw);
}

export async function fetchConversationTimeline(conversationId: string): Promise<DecisionItem[]> {
  const qs = new URLSearchParams({ conversation_id: conversationId }).toString();
  const raw = await api.get<unknown>(`/api/ai-console/decisions/timeline?${qs}`);
  return TimelineResponseSchema.parse(raw).data;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Lista de decisões com filtros e paginação cursor-based.
 * LGPD: dados chegam mascarados do backend — não logar decision/context.
 *
 * @param options.enabled - Quando false, a query não é disparada (útil para
 *   aguardar verificação de permissão antes de fazer fetch).
 */
export function useDecisionsList(
  filters: DecisionFilters,
  options?: { enabled?: boolean },
): {
  data: DecisionsListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: decisionsQueryKeys.list(filters),
    queryFn: () => fetchDecisionsList(filters),
    staleTime: 15_000,
    enabled: options?.enabled !== false,
  });

  return { data, isLoading, isError, error, refetch: () => void refetch() };
}

/**
 * Timeline cronológica de decisões de uma conversa específica.
 * Habilitado apenas quando conversationId não for vazio.
 *
 * @param options.enabled - Quando false, a query não é disparada (útil para
 *   aguardar verificação de permissão antes de fazer fetch).
 */
export function useConversationTimeline(
  conversationId: string,
  options?: { enabled?: boolean },
): {
  decisions: DecisionItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: decisionsQueryKeys.timeline(conversationId),
    queryFn: () => fetchConversationTimeline(conversationId),
    enabled: options?.enabled !== false && conversationId.length > 0,
    staleTime: 15_000,
  });

  return { decisions: data ?? [], isLoading, isError };
}
